/**
 * Prefstest Sticky — Dynamic Offset Manager
 * ───────────────────────────────────────
 * Detects all elements with `data-prefstest-sticky` and dynamically computes
 * `--prefstest-sticky-offset` so that stacked sticky elements don't overlap.
 *
 * Groups (via `data-prefstest-sticky-group`):
 *   "full-width"    — notice-bar, nav-primary, nav-secondary (vertical stack)
 *   "sidebar-left"  — left sidebar (desktop only)
 *   "sidebar-right" — right sidebar (desktop only)
 *
 * Sticky modes (via `data-prefstest-sticky-mode`):
 *   "always"    — always sticky (default if attr is present without mode)
 *   "scroll-up" — shows on scroll up, hides on scroll down
 *
 * Each element's offset = sum of heights of all preceding sticky elements
 * in the same group that are currently visible.
 *
 * Sidebar groups also account for full-width sticky height (they stack below
 * the full-width group).
 *
 * Recalculates on:
 *   - DOMContentLoaded / init
 *   - Window resize
 *   - Scroll (for scroll-up show/hide)
 *   - MutationObserver (for theme editor section additions/removals)
 *   - Element dismissal (notice-bar dismiss)
 *
 * No dependencies.
 */
(function () {
  'use strict';

  var ATTR_STICKY = 'data-prefstest-sticky';
  var ATTR_GROUP = 'data-prefstest-sticky-group';
  var ATTR_MODE = 'data-prefstest-sticky-mode';
  var VAR_OFFSET = '--prefstest-sticky-offset';
  var CLASS_HIDDEN = 'prefstest-sticky--hidden';
  var DESKTOP_BP = 990;

  /** @type {Element[]} All sticky elements, ordered by DOM position */
  var allSticky = [];

  /** Last known scroll Y for scroll-up detection */
  var lastScrollY = 0;

  /* ======================================================================
     1. Discovery — collect all [data-prefstest-sticky] in DOM order
     ====================================================================== */
  function discover() {
    allSticky = Array.prototype.slice.call(
      document.querySelectorAll('[' + ATTR_STICKY + ']')
    );
  }

  /* ======================================================================
     2. Resolve target element — for sidebars, the sticky target is the
        [data-prefstest-slot] ancestor (CSS Grid child), not the inner element.
        For full-width elements, the target IS the element itself.
     ====================================================================== */
  function resolveTarget(el) {
    var group = el.getAttribute(ATTR_GROUP) || 'full-width';
    if (group === 'sidebar-left' || group === 'sidebar-right') {
      var slot = el.closest('[data-prefstest-slot]');
      return slot || el;
    }
    return el;
  }

  /* ======================================================================
     3. Offset calculation — compute and apply --prefstest-sticky-offset
     ====================================================================== */
  function recalcOffsets() {
    var isDesktop = window.innerWidth >= DESKTOP_BP;

    // Group elements by their sticky group
    var groups = {};
    for (var i = 0; i < allSticky.length; i++) {
      var el = allSticky[i];
      var group = el.getAttribute(ATTR_GROUP) || 'full-width';

      // Skip sidebar groups on mobile
      if (!isDesktop && (group === 'sidebar-left' || group === 'sidebar-right')) {
        var target = resolveTarget(el);
        target.style.removeProperty(VAR_OFFSET);
        continue;
      }

      if (!groups[group]) groups[group] = [];
      groups[group].push(el);
    }

    // Calculate full-width total height (sidebars stack below full-width)
    var fullWidthTotal = 0;
    var fwElements = groups['full-width'] || [];
    for (var j = 0; j < fwElements.length; j++) {
      var fwEl = fwElements[j];
      var fwTarget = resolveTarget(fwEl);

      // Skip hidden/dismissed elements
      if (!isElementVisible(fwEl)) {
        fwTarget.style.setProperty(VAR_OFFSET, '0px');
        continue;
      }

      fwTarget.style.setProperty(VAR_OFFSET, fullWidthTotal + 'px');
      fullWidthTotal += getElementHeight(fwEl);
    }

    // Calculate offsets for other groups (sidebar-left, sidebar-right)
    var groupNames = Object.keys(groups);
    for (var g = 0; g < groupNames.length; g++) {
      var name = groupNames[g];
      if (name === 'full-width') continue;

      var cumulative = fullWidthTotal; // Start below full-width elements
      var elements = groups[name];

      for (var k = 0; k < elements.length; k++) {
        var sEl = elements[k];
        var sTarget = resolveTarget(sEl);

        if (!isElementVisible(sEl)) {
          sTarget.style.setProperty(VAR_OFFSET, '0px');
          continue;
        }

        sTarget.style.setProperty(VAR_OFFSET, cumulative + 'px');
        cumulative += getElementHeight(sEl);
      }
    }
  }

  /* ======================================================================
     4. Helpers
     ====================================================================== */
  function isElementVisible(el) {
    if (el.hidden) return false;
    if (el.offsetParent === null && getComputedStyle(el).position !== 'sticky' && getComputedStyle(el).position !== 'fixed') return false;
    var rect = el.getBoundingClientRect();
    // Consider element "visible" for offset purposes even when off-screen
    // but check if it's been dismissed (display: none)
    if (getComputedStyle(el).display === 'none') return false;
    // scroll-up hidden elements still reserve space in the stack since they
    // remain position: sticky and only use transform to slide out
    if (el.classList.contains(CLASS_HIDDEN)) return false;
    return true;
  }

  function getElementHeight(el) {
    return el.getBoundingClientRect().height;
  }

  /* ======================================================================
     5. Scroll-up behavior
     ====================================================================== */
  function handleScroll() {
    var currentY = window.pageYOffset || document.documentElement.scrollTop;
    var scrollUpEls = document.querySelectorAll('[' + ATTR_MODE + '="scroll-up"]');

    // In design mode: keep all sticky elements visible, skip scroll-up hide
    if (!(window.Shopify && window.Shopify.designMode)) {
      for (var i = 0; i < scrollUpEls.length; i++) {
        var el = scrollUpEls[i];
        if (currentY <= 0) {
          el.classList.remove(CLASS_HIDDEN);
        } else if (currentY > lastScrollY) {
          el.classList.add(CLASS_HIDDEN);
        } else {
          el.classList.remove(CLASS_HIDDEN);
        }
      }
    }

    lastScrollY = currentY;

    // Recalculate offsets since visibility may have changed
    recalcOffsets();
  }

  /* ======================================================================
     6. Resize handler — recalculate on window resize
     ====================================================================== */
  var resizeTimer;
  function handleResize() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      recalcOffsets();
    }, 100);
  }

  /* ======================================================================
     7. MutationObserver — re-discover when sections are added/removed
     ====================================================================== */
  function setupObserver() {
    if (!window.MutationObserver) return;

    var prefstest = document.querySelector('[data-prefstest]');
    if (!prefstest) return;

    var observer = new MutationObserver(function (mutations) {
      var shouldRediscover = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0 || mutations[i].removedNodes.length > 0) {
          shouldRediscover = true;
          break;
        }
        // Also watch for attribute changes on data-prefstest-sticky
        if (mutations[i].type === 'attributes' && mutations[i].attributeName === ATTR_STICKY) {
          shouldRediscover = true;
          break;
        }
      }
      if (shouldRediscover) {
        discover();
        recalcOffsets();
      }
    });

    observer.observe(prefstest, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [ATTR_STICKY, 'hidden', 'style']
    });
  }

  /* ======================================================================
     8. Dismiss watcher — notice-bar dismiss triggers recalc
     ====================================================================== */
  function setupDismissWatcher() {
    // Watch for hidden attribute changes on notice-bar (dismissed)
    document.addEventListener('click', function (event) {
      var dismissBtn = event.target.closest('[data-prefstest-notice-bar-dismiss]');
      if (!dismissBtn) return;

      // Small delay to let the dismiss animation/logic complete
      setTimeout(function () {
        recalcOffsets();
      }, 350);
    });
  }

  /* ======================================================================
     9. Initialization
     ====================================================================== */
  function init() {
    discover();

    if (allSticky.length === 0) return;

    recalcOffsets();

    // Scroll handler for scroll-up mode and offset recalc
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (!ticking) {
        requestAnimationFrame(function () {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    window.addEventListener('resize', handleResize, { passive: true });
    setupObserver();
    setupDismissWatcher();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
